import { jsx as _jsx } from "react/jsx-runtime";
import { memo } from "react";
// ---------------------------------------------------------------------------
// StampAnnotationView — inner visual for a stamp annotation.
//
// `MarkInteractiveView` (in `annotation-view.tsx`) renders the outer
// wrapper, gesture handlers, and resize/rotate handles. This file's
// job is just the inner glyph rendering — either a rubber-stamp text
// label (default) or a bare emoji reaction, switched via
// `annotation.content.style`.
//
// Sizing model (shared by both styles):
//   - The inner span renders at a FIXED `font-size` (24px label /
//     40px emoji — the modal-canonical size at scale 1.0). Padding,
//     letter-spacing, and border are also fixed pixel values picked
//     at this canonical size.
//   - The wrapper's `style.transform` (built by
//     `annotationTransformStyle`) carries ONLY the user's persisted
//     resize (`persistedScale = transform.height / BASE_HEIGHT_NORM`).
//   - Stream-vs-modal sizing is owned by `.annotations-container`
//     itself, which carries a `transform: scale(--annotation-natural-
//     scale)` that uniformly shrinks every annotation. The variable
//     is registered as `<number>` via `@property` and transitioned
//     by the modal panel from `0.5` to `1.0` over the FLIP — so the
//     entire annotation surface grows in lockstep with the panel
//     without any per-mark transition. No glyph re-rasterization
//     per frame, no end-of-animation "snap to final size."
//   - Width is intrinsic to the rendered glyphs (`width: max-content`
//     in CSS), so longer stamps get wider without any width axis to
//     persist.
//
// For the `label` style: color from the row applies via inline style
// on `color`, with the SCSS using `currentColor` for the border so a
// single style prop drives the whole stamp tint.
//
// For the `emoji` style: the color prop is irrelevant (emojis carry
// their own intrinsic glyph color) — we still pass it through inline
// for consistency, but the SCSS strips the border / uppercase / tilt
// so the emoji reads as a bare reaction glyph.
// ---------------------------------------------------------------------------
export const StampAnnotationView = memo(function StampAnnotationView({ annotation, }) {
    if (annotation.type !== "stamp")
        return null;
    // Default to "label" so legacy rows without the field render as
    // they did before this change. New emoji-reaction rows persist
    // `style: "emoji"` and pick up the bare-glyph CSS via the
    // modifier class below.
    const style = annotation.content.style ?? "label";
    const labelClassName = [
        "annotation-view__stamp-label",
        `annotation-view__stamp-label--style-${style}`,
    ].join(" ");
    return (_jsx("span", { className: labelClassName, style: { color: annotation.content.color }, "data-stamp-text": annotation.content.text, "data-stamp-style": style, children: annotation.content.text }));
});
